import { Component, OnDestroy, OnInit, effect, ViewChild } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Adr } from './adr.model';
import { ReferentialsAdrService } from './referentials-adr.service';
import { Table, TableModule } from 'primeng/table';
import { TableComponent } from '../../../../core/components/table/table.component';
import { TableReadOnlyComponent } from '../../../../core/components/table-read-only/table-read-only.component';
import { ButtonModule } from 'primeng/button';
import { AddAdrComponent } from './add-adr/add-adr.component';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { GenericErrorResponse } from '../../../../core/models/generic-response.model';
import { StoreService } from '../../../../core/services/store.service';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { IconFieldModule } from 'primeng/iconfield';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FilterEventService } from "../../../../core/services/filter-event.service";

const dialogConfig = {
  modal: true,
  width: '90rem',
  maximizable: true,
  breakpoints: {
    '1199px': '75vw',
    '575px': '90vw',
  },
};
@Component({
  selector: 'app-referentials-adr',
  standalone: true,
  imports: [TableModule, ButtonModule, CommonModule, FormsModule, InputTextModule, DynamicDialogModule, ToastModule, TagModule, IconFieldModule, DropdownModule, RippleModule, StyleClassModule, ReactiveFormsModule, TooltipModule, TranslocoModule],
  providers: [DialogService, MessageService, ReferentialsAdrService, provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './referentials-adr.component.html',
  styleUrl: './referentials-adr.component.css',
})
export class ReferentialsAdrComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private filterSubscription!: Subscription;
  @ViewChild('adrTable') table!: Table;

  selectedAdr!: Adr;
  adr!: Adr;
  filteredAdrData: Adr[] = [];

  errors: GenericErrorResponse[] = [];
  userHasReadWritePermission: boolean = false;

  ref: DynamicDialogRef | undefined;

  errorMessage?: string[];
  isFirstRun: boolean = true;
  lineSelected: boolean = false;
  totalRecords = 0;

  loading: boolean = false;

  adrResult!: Adr[];
  clonedAdr: { [s: string]: Adr } = {};

  adrForm = this.formBuilder.group({
    id: this.formBuilder.control<Number | null>(null),
    codeHecate: this.formBuilder.control<Number | null>(null),
    designation: [''],
    classe: [''],
    codeDanger: [''],
    codeTunnels: [''],
    groupeEmballage: [''],
    numeroUN: this.formBuilder.control<Number | null>(null),
  });

  constructor(
    private storeService: StoreService,
    private formBuilder: FormBuilder,
    private adrService: ReferentialsAdrService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private filterEventService: FilterEventService,
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.userHasReadWritePermission = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RW');
    this.loadAdr();
    this.filterSubscription = this.filterEventService.filterEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event === 'reset') {
        this.clearFilter();
      }
    });
  }

  private clearFilter() {
    this.table.clear();
  }

  private loadAdr() {
    this.loading = true;
    this.adrService
      .getAdrData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (adr) => {
          this.adrResult = adr;
          this.loading = false;
          this.totalRecords = adr.length;
        },
        error: (error: HttpErrorResponse) => this.handleHttpErrorResponse(error),
      });
  }

  editAdr(adr: Adr, index: number) {
    this.selectedAdr = adr;
    const id = adr.id;
    this.adrForm.controls.codeHecate.patchValue(adr.codeHecate);
    this.adrForm.controls.id.patchValue(adr.id);
    this.adrForm.controls.designation.patchValue(adr.designation);
    this.adrForm.controls.codeTunnels.patchValue(adr.codeTunnels);
    this.adrForm.controls.codeDanger.patchValue(adr.codeDanger);
    this.adrForm.controls.classe.patchValue(adr.classe);
    this.adrForm.controls.groupeEmballage.patchValue(adr.groupeEmballage);
    this.adrForm.controls.numeroUN.patchValue(adr.numeroUN);
    this.clonedAdr[id.toString()] = { ...adr };
  }

  saveRowAdr(adr: Adr, index: number) {
    this.adrService
      .updateAdrData(this.adrForm.value as Adr)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (inlineResponse) => {
          if (inlineResponse.success) {
            this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: 'La ligne a été modifiée avec succès.', life: 3000 });
            this.loadAdr();
          } else {
            this.errors = inlineResponse.errors.length > 0 ? inlineResponse.errors : [inlineResponse.exception];
            this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue ' + this.errors.map((e) => e.errorMsg), life: 3000 });
            this.loadAdr();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
        },
      });
  }

  cancelEditRowAdr(adr: Adr, index: number) {
    const id = adr.id;
    this.adrResult
      .filter((c) => c.id === id)
      .map((adr) => {
        adr = this.clonedAdr[id.toString()];
      });
    delete this.clonedAdr[id.toString()];
  }

  addNewAdr() {
    this.ref = this.dialogService.open(AddAdrComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
      data: {
        adrForm: this.adrForm,
        userHasReadWritePermission: this.userHasReadWritePermission,
      },
    });

    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.loadAdr();
      }
    });
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
